import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { ToastService } from 'src/app/services/toast.service';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { SynergyDetailSheetComponent, SynergyDetailData } from './synergy-detail-sheet/synergy-detail-sheet.component';

interface Synergy {
  id: number;
  title: string;
  description: string;
  category: string;
  status: string;
  match_score: number;
  smart_coins_earned: number;
  is_internal: boolean;
  hub_1: any;
  hub_2: any;
  both_confirmed: boolean;
  created_at: string;
  activated_at?: string;
  potential_benefits: string[];
}

interface Metrics {
  total: number;
  internal: number;
  external: number;
  active: number;
  pending: number;
}
@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    SynergyDetailSheetComponent
  ],
  selector: 'app-partner-synergies',
  templateUrl: './synergies.component.html',
  styleUrls: ['./synergies.component.scss']
})
export class SynergiesComponent implements OnInit {
  synergies: Synergy[] = [];
  filteredSynergies: Synergy[] = [];

  isLoading = true;
  searchTerm = '';
  selectedType: string | null = null;
  selectedStatus: string | null = null;

  // Sheet state
  selectedSynergy: SynergyDetailData | null = null;
  sheetVisible = false;
  isDesktop = window.innerWidth >= 1024;

  @HostListener('window:resize')
  onResize() {
    this.isDesktop = window.innerWidth >= 1024;
  }

  constructor(
    private http: HttpClient,
    private toast: ToastService
  ) { }

  async ngOnInit() {
    await this.loadSynergies();
  }

  async loadSynergies() {
    try {
      this.isLoading = true;
      const response = await firstValueFrom(
        this.http.get<{ status: string; synergies: Synergy[]; count: number; metrics: Metrics }>(
          `${environment.apiUrl}/regional/synergies/overview`
        )
      );

      this.synergies = response.synergies;
      this.filteredSynergies = [...this.synergies];
    } catch (error) {
      console.error('Error loading synergies:', error);
      this.toast.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Synergien konnten nicht geladen werden'
      });
    } finally {
      this.isLoading = false;
    }
  }

  onSearch() {
    this.applyFilters();
  }

  clearFilters(): void {
    this.selectedType = null;
    this.selectedStatus = null;
    this.applyFilters();
  }

  setTypeFilter(type: string): void {
    this.selectedType = this.selectedType === type ? null : type;
    this.selectedStatus = null;
    this.applyFilters();
  }

  setStatusFilter(status: string): void {
    this.selectedStatus = this.selectedStatus === status ? null : status;
    this.selectedType = null;
    this.applyFilters();
  }

  getCategoryLabel(category: string): string {
    const labels: { [key: string]: string } = {
      'supply-chain': 'Supply Chain',
      'energy': 'Energie',
      'mobility': 'Mobilität',
      'innovation': 'Innovation',
      'service': 'Service',
      'marketing': 'Marketing',
      'resources': 'Ressourcen'
    };
    return labels[category] || category;
  }

  applyFilters() {
    this.filteredSynergies = this.synergies.filter(synergy => {
      const matchesSearch = !this.searchTerm ||
        synergy.title.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        synergy.hub_1.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        synergy.hub_2.name.toLowerCase().includes(this.searchTerm.toLowerCase());

      const matchesType = !this.selectedType ||
        (this.selectedType === 'internal' && synergy.is_internal) ||
        (this.selectedType === 'external' && !synergy.is_internal);

      const matchesStatus = !this.selectedStatus || synergy.status === this.selectedStatus;

      return matchesSearch && matchesType && matchesStatus;
    });
  }

  openDetail(synergy: SynergyDetailData): void {
    this.selectedSynergy = synergy;
    this.sheetVisible = true;
  }

  closeDetail(): void {
    this.sheetVisible = false;
    this.selectedSynergy = null;
  }
}